import { LoaderFunction } from 'react-router-dom';
import debug from 'debug';

const logDiag = debug('rois:loaders:machinery:validation');
if (import.meta.env.DEV) {
  debug.enable('rois:loaders:machinery:*');
}

// Sentry
import * as Sentry from '@sentry/react';

// GraphQL
import {
  FETCH_AUX_MACHINE_TYPE_BY_ID,
  FETCH_AUX_MACHINE_TYPES,
  FETCH_AUX_MACHINES,
} from '../../../../../gql/queries/actions/auxMachines';
import { client } from '../../../../../gql/client';
import { AuthContextType } from '../../../../../hooks/useAuth';
import { selectMachineriesValue } from '../../../../../gql/selectors/actions';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../../../components/forms/ActionForms/constants';

// Utils
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import {
  GET_MEASURE_TYPES_BY_ID,
  FETCH_MEASURE_TYPES,
} from '../../../../../gql/queries/actions/measureTypes';
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';
import { GET_ACTION_BY_ID } from '../../../../../gql/queries/actions/actions';
import { FILTER_VEHICLES } from '../../../../../gql/queries/vehicles';
import { GET_VEHICLE_CATEGORIES } from '../../../../../gql/queries/vehicleCategory';
import { GET_CONCESSIONS } from '../../../../../gql/queries/orders/orders';
import { FETCH_TEAMS } from '../../../../../gql/queries/users';

// ⚠️ SINCRONIZACIÓN: Sistema de exclusiones
import { getExcludedVehicles } from '../../../../../utils/excludedVehiclesSync';
import { reconcileValidationActionContext } from '../../../../../utils/validationActionContext';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import { ACTION_MEDIA_SCOPE } from '../../../../../utils/draftScopes';

// ⚠️ Tipo de retorno de loadFormValues
type LoadedFormValues = {
  isWorkEquipmentDisplayed: string;
  isMachineryTableDisplayed?: string;
  machineryTable: Record<string, any>;
  vehiclesTable: Record<string, any>;
};

const loadFormValues = async (
  userData: AuthContextType | null,
  actionId: string
): Promise<LoadedFormValues | null> => {
  if (userData?.user) {
    try {
      const formValues = await selectMachineriesValue(actionId);
      return formValues;
    } catch (err) {
      Sentry.captureException(err);
      console.log(`> Error loading machinery values`, err);
    }
  }

  return null;
};

export const MachineryValidationLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async ({ params }) => {
  // La URL manda: si el contexto de la pestaña es de OTRA actuación se borra
  // entero y esta carga sale por el `:id` de la URL. Síncrono y antes de
  // cualquier `await` (diagnóstico 2026-10-08 § 8).
  reconcileValidationActionContext(params.id);
  // La lista de fotos y el croquis en curso también son de la actuación
  // de la URL. Las carpetas de OPFS no se tocan: las comparten las
  // pestañas (spec 2026-11-02 wizard-draft-ownership § 6).
  claimDraftScope(ACTION_MEDIA_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const stringifyForm = sessionStorage.getItem(
    formActionValidationNaming.NEW_VALIDATION_WORK_EQUIPMENT_FORM
  );

  const stringifyActionData = sessionStorage.getItem(
    formActionNaming.NEW_VALIDATION_ACTION_DATA
  );

  let sessionStorageData;
  let actionData;
  let parentExpeditionOrderStatus;
  let state;

  if (stringifyForm) {
    sessionStorageData = JSON.parse(decompressSessionStorage(stringifyForm));
  }

  if (stringifyActionData) {
    actionData = JSON.parse(decompressSessionStorage(stringifyActionData));
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: actionData.orderId,
      },
    });
    const parentOrder = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });
    parentExpeditionOrderStatus = parentOrder.data.order.state;
    state = order.state;
  } else if (params.id) {
    // BUG #5 FIX: When navigating via double-click, sessionStorage doesn't have actionData
    const {
      data: { action },
    } = await client.query({
      query: GET_ACTION_BY_ID,
      variables: { id: params.id },
    });

    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: action.orderIDs[0] },
    });

    actionData = {
      id: params.id,
      orderId: order.id,
      roid: order.roid,
      isActionEdited: 'false',
    };

    const parentOrder = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });
    parentExpeditionOrderStatus = parentOrder.data.order.state;
    state = order.state;
  }

  // ✅ Cargar todos los datos en paralelo para mejor rendimiento
  const [
    {
      data: { filterVehicles: vehiclesUnsorted },
    },
    {
      data: { auxMachines: auxMachineUnsorted },
    },
    {
      data: { vehicleCategories },
    },
    {
      data: { concessions },
    },
    {
      data: { auxMachineTypes },
    },
    {
      data: { measureTypes },
    },
    {
      data: { teams },
    },
  ] = await Promise.all([
    client.query({
      query: FILTER_VEHICLES,
      variables: {
        input: {
          isActive: true,
        },
      },
      fetchPolicy: 'network-only', // ✅ Siempre traer datos frescos del servidor tras reload
    }),
    client.query({
      query: FETCH_AUX_MACHINES,
      fetchPolicy: 'network-only', // ✅ Siempre traer datos frescos del servidor tras reload
    }),
    client.query({
      query: GET_VEHICLE_CATEGORIES,
    }),
    client.query({
      query: GET_CONCESSIONS,
    }),
    client.query({
      query: FETCH_AUX_MACHINE_TYPES,
    }),
    client.query({
      query: FETCH_MEASURE_TYPES,
    }),
    client.query({
      query: FETCH_TEAMS,
      fetchPolicy: 'network-only',
    }),
  ]);

  const auxMachinesData = await Promise.all(
    auxMachineUnsorted.map(
      async (machine: {
        id: string;
        auxMachineId: string;
        description: string;
        type: string;
        typeOfMeasurement: string;
        isInternal?: boolean;
        measureTypeIDs: string[];
        auxMachineTypeIDs: string[];
      }) => {
        const [
          {
            data: { measureType },
          },
          {
            data: { auxMachineType },
          },
        ] = await Promise.all([
          client.query({
            query: GET_MEASURE_TYPES_BY_ID,
            variables: {
              id: machine?.measureTypeIDs[0],
            },
            fetchPolicy: 'cache-first',
          }),
          client.query({
            query: FETCH_AUX_MACHINE_TYPE_BY_ID,
            variables: {
              id: machine?.auxMachineTypeIDs[0],
            },
            fetchPolicy: 'cache-first',
          }),
        ]);
        return {
          id: machine.id,
          auxMachineId: machine.auxMachineId,
          description: machine.description,
          type: auxMachineType.name,
          typeOfMeasurement: measureType.name,
          isInternal:
            machine.isInternal !== undefined ? machine.isInternal : true,
        };
      }
    )
  );
  const auxMachines = [...auxMachinesData].sort((a, b) =>
    a.auxMachineId.localeCompare(b.auxMachineId)
  );

  // 🔍 [DIAG] Log raw vehicles with teamIDs to compare prod vs beta
  logDiag('vehiclesUnsorted total: %d %O', vehiclesUnsorted.length,
    vehiclesUnsorted.map((v: any) => ({ plate: v.plate, usage: v.usage, isActive: v.isActive, teamIDs: v.teamIDs || [] }))
  );

  // ✅ Construir mapa vehicleID → teams (M2M) para enriquecer vehículos con data de equipo
  const vehicleTeamMap = new Map<string, Array<{ id: string; name: string }>>();
  (teams || []).forEach((team: { id: string; name: string; vehicleIDs: string[] }) => {
    (team.vehicleIDs || []).forEach((vehicleId: string) => {
      const existing = vehicleTeamMap.get(vehicleId) || [];
      vehicleTeamMap.set(vehicleId, [...existing, { id: team.id, name: team.name }]);
    });
  });

  // ✅ NUEVO: Filtrar SOLO vehículos de construcción (Maquinaria de Obra)
  const vehicles = vehiclesUnsorted
    .filter((vehicle: any) => vehicle.usage === 'CONSTRUCTION')
    .map((vehicle: any) => {
      const vehicleTeams = vehicleTeamMap.get(vehicle.id) || [];
      return {
        id: vehicle.id,
        plate: vehicle.plate || '',
        model: vehicle.model || '',
        category: vehicle.category || '',
        ownership: vehicle.ownership, // INTERNAL o EXTERNAL
        usage: vehicle.usage, // CONSTRUCTION
        teams: vehicleTeams.map((t) => t.name),
        teamIDs: vehicleTeams.map((t) => t.id),
      };
    })
    .sort((a: any, b: any) => a.plate.localeCompare(b.plate));

  // 🔍 [DIAG] Log filtered CONSTRUCTION vehicles
  logDiag('vehicles CONSTRUCTION filtrados: %d %O', vehicles.length,
    vehicles.map((v: any) => ({ plate: v.plate, ownership: v.ownership, teams: v.teams }))
  );

  // ⚠️ SINCRONIZACIÓN: Pre-cargar vehículos con conductor desde OperatorsAndVehicles
  const operatorsStorageKey =
    formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM;
  const operatorsDataRaw = sessionStorage.getItem(operatorsStorageKey);

  let vehiclesWithDrivers: any[] = [];
  if (operatorsDataRaw) {
    const operatorsData = JSON.parse(
      decompressSessionStorage(operatorsDataRaw)
    );
    const vehicleTable = operatorsData?.vehicleTable || {};

    // ⚠️ VALIDACIÓN 1: Solo vehículos que existen en Apollo cache
    vehiclesWithDrivers = Object.values(vehicleTable)
      .filter((v: any) => {
        // Debe tener vehicleID y driverID
        if (!v.vehicleID || !v.driverID) return false;

        // ⚠️ CRÍTICO: Verificar que el vehículo existe en la lista cargada
        return vehicles.some((vehicle: any) => vehicle.id === v.vehicleID);
      })
      .map((v: any) => {
        const fullVehicle = vehicles.find(
          (vehicle: any) => vehicle.id === v.vehicleID
        );
        return {
          id: fullVehicle.id,
          plate: fullVehicle.plate,
          model: fullVehicle.model,
          category: fullVehicle.category,
          ownership: fullVehicle.ownership,
          usage: fullVehicle.usage,
        };
      });
  }

  const loaderToResolve = await loadFormValues(userData, actionData.id);

  // ⚠️ VALIDACIÓN 2: Obtener vehículos excluidos explícitamente por el usuario
  const excludedVehicles = getExcludedVehicles(true); // true = Validation
  console.log(
    `📦 [MachineryValidationLoader] Vehículos excluidos por el usuario: ${excludedVehicles.length}`,
    excludedVehicles
  );

  // ⚠️ VALIDACIÓN 3: Merge sin duplicados y sin excluidos
  // vehiclesTable debe ser un objeto Record<string, WorkVehicle>
  const vehiclesTableObject =
    sessionStorageData?.vehiclesTable || loaderToResolve?.vehiclesTable || {};
  const existingIds = new Set(
    Object.values(vehiclesTableObject).map((v: any) => v.id || v.vehicleId)
  );

  const uniqueVehiclesWithDrivers = vehiclesWithDrivers.filter((pv: any) => {
    // ⚠️ CRÍTICO: NO añadir si ya existe O si está explícitamente excluido
    return !existingIds.has(pv.id) && !excludedVehicles.includes(pv.id);
  });

  // ✅ Mantener como objeto Record<string, WorkVehicle>
  // ✅ Usar el ID del vehículo como clave directamente (sin prefijos ni timestamps)
  const mergedVehiclesTable = { ...vehiclesTableObject };
  uniqueVehiclesWithDrivers.forEach((v: any) => {
    const vehicleKey = v.id; // ✅ Simplemente el ID
    mergedVehiclesTable[vehicleKey] = {
      vehicleId: v.id,
      plate: v.plate,
      model: v.model,
      category: v.category,
      ownership: v.ownership,
      usage: v.usage,
    };
  });

  console.log(
    `📦 [MachineryValidationLoader] Pre-carga: ${uniqueVehiclesWithDrivers.length} vehículos con conductor añadidos, ` +
      `${vehiclesWithDrivers.length - uniqueVehiclesWithDrivers.length} duplicados/excluidos evitados`
  );

  const combinedData = {
    ...loaderToResolve,
    ...sessionStorageData,
    ...actionData,
    parentExpeditionOrderStatus,
    state,
    // ✅ Sobrescribir vehiclesTable con la versión mergeada
    vehiclesTable: mergedVehiclesTable,

    vehicles: vehicles.map((vehicle: any) => ({
      ...vehicle,
      vehicleOccupantId: null,
    })),
    machines: auxMachines.map((auxMachine: any) => ({
      ...auxMachine,
      auxMachineUnitId: null,
    })),
    vehicleCategories,
    concessions,
    auxMachineTypes,
    measureTypes,
    teams: (teams || []).map((t: { id: string; name: string }) => ({ id: t.id, name: t.name })),
    dataFromServer: {
      ...loaderToResolve,
      vehicles,
      machines: auxMachines,
    },
  };

  return combinedData;
};

  return { data: load() };
};
